import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import images from 'assets/sheets/images';
import PropTypes from 'prop-types';
import {
  SelectRow,
  SelectLabel,
  ErrorMessage,
  DropdownContainer,
  DropdownControl,
  DropdownItem,
  ValueText,
  IconWrapper,
  FloatingList,
} from './styled';

const Select = forwardRef(
  (
    {
      label,
      title,
      required,
      rules = [],
      normalize,
      rounded,
      placeholder,
      value,
      onChange,
      disabled,
      isShowCurrent = true,
      widthLabel,
      widthSelect,
      isPopup = true,
      isWebkit = false,
      heightSelect,
      onTable,
      pagination = false,
      showOptions,
      name,
      id,
      className,
      hiddenPlaceholder,
      classNameSelect,
      classNameLabel,
      classNameValue,
      style,
      textColor,
      sizeIcon,
      onValidationChange,
      isSubmitted,
      errors,
      $paddingMessage,
      noDataText,
      options = [],
      bordered = true,
      widthList,
      classNameSelectRow,
      $iconDown,
      ...rest
    },
    ref
  ) => {
    const selectId = id || name;
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
      const errorForThisInput = errors?.find((err) => err.label === title);
      if (errorForThisInput) {
        setError(true);
        setErrorMessage(errorForThisInput.message);
      } else {
        setError(false);
        setErrorMessage('');
      }
    }, [errors, title]);

    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const controlRef = useRef(null);
    const listRef = useRef(null);
    const [position, setPosition] = useState({
      top: 0,
      left: 0,
      width: undefined,
      maxHeight: 240,
    });

    const allRules = [
      ...(required
        ? [
            {
              required: true,
              message: `${label || 'This field'} is required`,
            },
          ]
        : []),
      ...rules,
    ];

    const validateValue = async (val) => {
      const normalizedValue = normalize ? normalize(val) : val;

      for (const rule of allRules) {
        // Required validation (only if submitted)
        if (isSubmitted && rule.required && !normalizedValue) {
          setError(true);
          setErrorMessage(
            rule.message || `${label || 'This field'} is required`
          );
          onValidationChange && onValidationChange(false, rule.message);
          return false;
        }

        if (rule.validator && normalizedValue !== undefined) {
          try {
            await rule.validator(null, normalizedValue);
          } catch (errorMsg) {
            setError(true);
            setErrorMessage(errorMsg);
            onValidationChange && onValidationChange(false, errorMsg);
            return false;
          }
        }

        if (rule.pattern && normalizedValue) {
          const str = `${normalizedValue}`;
          if (!rule.pattern.test(str)) {
            setError(true);
            setErrorMessage(rule.message || 'Invalid format');
            onValidationChange && onValidationChange(false, rule.message);
            return false;
          }
        }
      }

      setError(false);
      setErrorMessage('');
      onValidationChange && onValidationChange(true, '');
      return true;
    };

    // Validate when isSubmitted changes
    useEffect(() => {
      if (isSubmitted) {
        validateValue(value);
      }
    }, [isSubmitted, value]);

    // Close when clicking outside or scrolling
    useEffect(() => {
      const onDocClick = (e) => {
        const inPortal =
          e.target &&
          e.target.closest &&
          e.target.closest('[data-select-portal="true"]');
        if (!containerRef.current.contains(e.target) && !inPortal) {
          if (open) setOpen(false);
        }
      };

      const handleScroll = (e) => {
        if (open && listRef.current && !listRef.current.contains(e.target)) {
          setOpen(false);
        }
      };

      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('scroll', handleScroll, { capture: true });

      return () => {
        document.removeEventListener('mousedown', onDocClick);
        document.removeEventListener('scroll', handleScroll, { capture: true });
      };
    }, [open]);

    useLayoutEffect(() => {
      const updatePosition = () => {
        if (!open || !controlRef.current) return;

        const { bottom, top, left, width } =
          controlRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - bottom;
        const spaceAbove = top;

        const base = {
          left,
          width: widthList || width,
          maxHeight: 240,
        };

        // Prefer opening where space is larger (or enough for 240px)
        const openDown = spaceBelow >= 240 || spaceBelow >= spaceAbove;

        if (openDown) {
          setPosition({
            ...base,
            top: bottom + window.scrollY + 6,
            maxHeight: Math.min(spaceBelow, 240),
          });
        } else {
          const maxH = Math.min(spaceAbove, 240);
          // First set using max height, then correct using actual rendered height
          setPosition({
            ...base,
            top: top + window.scrollY - maxH,
            maxHeight: maxH,
          });

          requestAnimationFrame(() => {
            if (!listRef.current) return;
            const actualH = Math.min(
              listRef.current.offsetHeight || maxH,
              maxH
            );
            setPosition({
              ...base,
              top: top + window.scrollY - actualH - 6,
              maxHeight: maxH,
            });
          });
        }
      };

      if (open) {
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
      }

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }, [open, widthList]);

    const selectedOption = (options || []).find(
      (o) => `${o.value}` === `${value}` || o.label === value
    );

    const handleSelectOption = (opt) => {
      if (opt.disabled || disabled) return;
      const newValue = opt?.value;
      const normalizedValue = normalize ? normalize(newValue) : newValue;
      if (onChange) {
        onChange({ label: title, value: normalizedValue });
      }
      setOpen(false);
    };

    // Filter out props that shouldn't be passed to DOM elements
    /* eslint-disable no-unused-vars */
    const {
      iconDown,
      dropdownRender,
      dropdownMatchSelectWidth,
      dropdownStyle,
      popupClassName,
      suffixIcon,
      showArrow,
      popupMatchSelectWidth,
      ...cleanRest
    } = rest;
    /* eslint-enable no-unused-vars */

    return (
      <div className={`${className} items-center justify-center`} style={style}>
        <SelectRow className={`${classNameSelectRow}`}>
          {label && (
            <SelectLabel
              $isWebkit={isWebkit}
              $isPopup={isPopup}
              htmlFor={selectId}
              $widthLabel={widthLabel}
              $widthLabelPopup={widthLabel}
              className={`${classNameLabel} ${textColor}`}
            >
              {label}
              {required && <span className="required">*</span>}
            </SelectLabel>
          )}
          <DropdownContainer
            $width={widthSelect}
            ref={containerRef}
            className={''}
            $isPopup={isPopup}
            $iconDown={$iconDown}
            {...cleanRest}
          >
            <DropdownControl
              ref={(node) => {
                controlRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
              }}
              role="button"
              aria-haspopup="listbox"
              aria-expanded={open}
              onTable={onTable}
              tabIndex={0}
              onClick={() => (!disabled ? setOpen((s) => !s) : null)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen((s) => !s);
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              disabled={disabled}
              $width={widthSelect}
              $height={heightSelect}
              $isPopup={isPopup}
              rounded={rounded}
              $bordered={bordered}
              className={`${classNameSelect} ${error ? 'error' : ''} ${open ? 'open' : ''}`}
              $error={error}
            >
              <ValueText
                $placeholder={!selectedOption && !!placeholder}
                disabled={disabled}
                className={classNameValue}
              >
                {selectedOption ? selectedOption.label : placeholder || ''}
              </ValueText>
              <IconWrapper
                style={{ width: sizeIcon || 16, height: sizeIcon || 24 }}
              >
                <images.dropDownIcon.src />
              </IconWrapper>
            </DropdownControl>

            {open &&
              ReactDOM.createPortal(
                <FloatingList
                  ref={listRef}
                  role="listbox"
                  data-select-portal="true"
                  top={position.top}
                  left={position.left}
                  width={position.width}
                  $maxHeight={position.maxHeight}
                >
                  {hiddenPlaceholder &&
                    placeholder !== undefined &&
                    options.length > 0 && (
                      <DropdownItem
                        role="option"
                        aria-selected={!value}
                        onClick={() =>
                          handleSelectOption({ value: '', label: placeholder })
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleSelectOption({
                              value: '',
                              label: placeholder,
                            });
                          }
                        }}
                        tabIndex={0}
                        className={!value ? 'selected' : ''}
                      >
                        {value || placeholder}
                      </DropdownItem>
                    )}
                  {options.length === 0 ? (
                    <DropdownItem
                      role="option"
                      aria-disabled
                      tabIndex={-1}
                      className="disabled"
                    >
                      {noDataText || 'No data'}
                    </DropdownItem>
                  ) : showOptions ? (
                    options
                      .filter((o) => showOptions.includes(o.value))
                      .filter(
                        (opt) => isShowCurrent || `${opt.value}` !== `${value}`
                      )
                      .map((opt) => (
                        <DropdownItem
                          key={opt.value}
                          role="option"
                          pagination={true}
                          aria-selected={`${opt.value}` === `${value}`}
                          onClick={() => handleSelectOption(opt)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleSelectOption(opt);
                            }
                          }}
                          tabIndex={0}
                          className={
                            `${opt.value}` === `${value}` ? 'selected' : ''
                          }
                        >
                          {opt.label}
                        </DropdownItem>
                      ))
                  ) : (
                    options
                      .filter(
                        (opt) => isShowCurrent || `${opt.value}` !== `${value}`
                      )
                      .map((opt, index) => (
                        <DropdownItem
                          key={index}
                          role="option"
                          pagination={pagination}
                          aria-selected={`${opt.value}` === `${value}`}
                          onClick={() => handleSelectOption(opt)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleSelectOption(opt);
                            }
                          }}
                          tabIndex={0}
                          className={
                            `${opt.value}` === `${value}` ? 'selected' : ''
                          }
                        >
                          {opt.label}
                        </DropdownItem>
                      ))
                  )}
                </FloatingList>,
                document.body
              )}
          </DropdownContainer>
        </SelectRow>
        {error && (
          <ErrorMessage $paddingMessage={$paddingMessage}>
            {errorMessage}
          </ErrorMessage>
        )}
      </div>
    );
  }
);

Select.propTypes = {
  label: PropTypes.string,
  title: PropTypes.string,
  required: PropTypes.bool,
  rules: PropTypes.array,
  normalize: PropTypes.func,
  placeholder: PropTypes.string,
  hiddenPlaceholder: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  widthLabel: PropTypes.number,
  widthSelect: PropTypes.number,
  widthList: PropTypes.string,
  heightSelect: PropTypes.number,
  name: PropTypes.string,
  onTable: PropTypes.bool,
  sizeIcon: PropTypes.number,
  id: PropTypes.string,
  className: PropTypes.string,
  classNameSelect: PropTypes.string,
  classNameLabel: PropTypes.string,
  classNameValue: PropTypes.string,
  style: PropTypes.object,
  onValidationChange: PropTypes.func,
  isPopup: PropTypes.bool,
  classNameSelectRow: PropTypes.string,
  pagination: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  errors: PropTypes.array,
  $paddingMessage: PropTypes.number,
  noDataText: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      disabled: PropTypes.bool,
    })
  ),
  $iconDown: PropTypes.elementType,
};

Select.displayName = 'Select';

export default Select;
